/** Where the letterhead's mark sits. */
export const LETTERHEAD_STYLES = [
  'rule',
  'split',
  'inline',
  'plate',
  'crest',
  'spread',
  'measured',
] as const

export type LetterheadStyle = (typeof LETTERHEAD_STYLES)[number]

/** What the letterhead's mark is. */
export const LETTERHEAD_RULES = ['single', 'double'] as const
export type LetterheadRule = (typeof LETTERHEAD_RULES)[number]

/** Which ink draws the letterhead's mark. */
export const LETTERHEAD_INKS = ['neutral', 'gold'] as const
export type LetterheadInk = (typeof LETTERHEAD_INKS)[number]

/** How loud the head is. */
export const LETTERHEAD_SIZES = ['small', 'medium', 'large'] as const
export type LetterheadSize = (typeof LETTERHEAD_SIZES)[number]

/** How much room the artifact gives the head. */
export const MASTHEAD_FORMS = ['full', 'compact'] as const
export type MastheadForm = (typeof MASTHEAD_FORMS)[number]

type LetterheadRecipe = {
  key: string
  label: string
  description: string
  style: LetterheadStyle
  rule: LetterheadRule
  ink: LetterheadInk
  size: LetterheadSize
  compact: { style: LetterheadStyle; size: LetterheadSize }
}

/** The complete stationery looks a congregation can choose from. */
export const LETTERHEAD_PRESETS: LetterheadRecipe[] = [
  {
    key: 'plain',
    label: 'Plain',
    description: 'Name over the Rav, closed by a single line.',
    style: 'rule',
    rule: 'single',
    ink: 'neutral',
    size: 'medium',
    compact: { style: 'inline', size: 'small' },
  },
  {
    key: 'engraved',
    label: 'Engraved',
    description: 'The same, set large and closed by a double rule.',
    style: 'rule',
    rule: 'double',
    ink: 'neutral',
    size: 'large',
    compact: { style: 'inline', size: 'small' },
  },
  {
    key: 'banner',
    label: 'Banner',
    description: 'The name sits inside the line, which runs to both margins.',
    style: 'split',
    rule: 'double',
    ink: 'neutral',
    size: 'large',
    compact: { style: 'split', size: 'small' },
  },
  {
    key: 'plate',
    label: 'Plate',
    description: 'Name and Rav inside a hairline panel.',
    style: 'plate',
    rule: 'single',
    ink: 'neutral',
    size: 'medium',
    compact: { style: 'plate', size: 'small' },
  },
  {
    key: 'crest',
    label: 'Crest',
    description: 'A short gold line under the name. Nothing crosses the sheet.',
    style: 'crest',
    rule: 'single',
    ink: 'gold',
    size: 'large',
    compact: { style: 'crest', size: 'small' },
  },
  {
    key: 'balanced',
    label: 'Balanced',
    description: 'Head and address ruled to the width of the schedule.',
    style: 'measured',
    rule: 'single',
    ink: 'neutral',
    size: 'large',
    compact: { style: 'measured', size: 'small' },
  },
  {
    key: 'spread',
    label: 'Spread',
    description: 'Congregation and Rav at opposite ends of one line.',
    style: 'spread',
    rule: 'single',
    ink: 'neutral',
    size: 'medium',
    compact: { style: 'spread', size: 'small' },
  },
]

export const DEFAULT_LETTERHEAD = 'plain'

/** The classes a stored letterhead puts on an artifact's root. */
export function letterheadClasses(
  letterhead: string | null | undefined,
  masthead: MastheadForm = 'full',
): string[] {
  const preset =
    LETTERHEAD_PRESETS.find((candidate) => candidate.key === letterhead) ??
    LETTERHEAD_PRESETS[0]
  const style = masthead === 'compact' ? preset.compact.style : preset.style
  const size = masthead === 'compact' ? preset.compact.size : preset.size
  return [
    'shul-stationery',
    `shul--lh-${style}`,
    `shul--lhr-${preset.rule}`,
    `shul--lhi-${preset.ink}`,
    `shul--lhs-${size}`,
    `shul--mh-${masthead}`,
  ]
}
